import merge from 'lodash/merge';
import has from 'lodash/has';
import dataProvider from './JSONServerDataProvider';
// eslint-disable-next-line import/extensions
import config from '../config/config';
import { GET_LIST, GET_ONE, CREATE, UPDATE, DELETE } from '../containers/App/constants';
import { fetchJson } from './fetch';
import route from '../constants/routes';
import {
  ACCOUNT_SETTINGS,
  COMPETITIONS,
  FILTER_AGGREGATION_SEARCH,
  PRODUCTIONS_AGGREGATIONS,
} from '../constants/endpointConstants';
import { getServerContext } from './serverContext';
import { ARTIST_TYPE_ID } from '../constants/consts';
import { getCookieUser } from './auth';
import { getCookie } from './cookie';
import { COOKIE_USER } from '../constants/cookieConstants';

const httpClient = (uri, options = {}) => {
  const serverContext = getServerContext();
  let user;
  let url;
  if (!process.browser && serverContext) {
    url = `http://localhost:3000/api/${uri}`;

    if (has(serverContext.req, 'headers.cookie')) {
      const browserCookies = {
        cookie: serverContext.req.headers.cookie,
      };
      // eslint-disable-next-line no-param-reassign
      options.headers = { ...options.headers, ...browserCookies };
    }

    user = getCookieUser(serverContext);
  } else {
    url = `${config.FRONTBASE_URL}/api/${uri}`;
    user = getCookie(COOKIE_USER);
  }
  if (user) {
    // eslint-disable-next-line no-param-reassign
    options.user = typeof user === 'string' ? JSON.parse(user) : user;
  }
  return fetchJson(url, options);
};

const Provider = dataProvider(httpClient);

const login = (email, password, isTrustedDevice) =>
  Provider(CREATE, 'login', {
    data: {
      user: {
        email,
        password,
      },
      type: 'user',
      isTrustedDevice,
      application: 'V3',
    },
  });

const autoLogin = token =>
  Provider(CREATE, 'login', {
    data: {
      type: 'autoLogin',
      autoLogin: {
        token,
      },
      application: 'V3',
    },
  });

const refreshToken = query =>
  Provider(UPDATE, 'login/refresh-token', {
    data: {
      type: 'user',
    },
    queryParams: {
      token: query,
    },
  });

const getConstants = () => Provider(GET_LIST, 'login/constants', {});

const getUser = userId =>
  Provider(GET_ONE, 'users', {
    id: userId,
  });

const getProfilePermissions = profileId =>
  Provider(GET_ONE, 'profiles/permissions', {
    id: profileId,
  });

const getUserPermissions = userId =>
  Provider(GET_ONE, 'users/permissions', {
    id: userId,
  });

const getWork = (workId, query) =>
  Provider(GET_ONE, 'works', {
    id: workId,
    queryParams: query,
  });

const getProfiles = (userId, showHidden, showInvalid) =>
  Provider(GET_LIST, 'profiles', {
    queryParams: {
      access_user_id: userId,
      show_hidden: !!showHidden,
      show_invalid: !!showInvalid,
      limit: 100,
    },
  });

const getAccessRequests = () =>
  Provider(GET_LIST, 'profiles/accessrequests', {
    queryParams: {
      limit: 100,
    },
  });

const getProfileData = (profileId, query = null) =>
  Provider(GET_ONE, 'profiles', {
    id: profileId,
    queryParams: query,
  });

const getAccessRequestByToken = token =>
  Provider(GET_LIST, 'profiles/profileaccessrequests', {
    queryParams: { link_token: token },
  });

const getActiveProfile = (userId, query) =>
  Provider(GET_ONE, 'settings', {
    id: userId,
    queryParams: {
      ...query,
      exactSetting: 'active_profile_id',
    },
  });

const registerUser = data =>
  Provider(CREATE, 'users', {
    data,
  });

const validateEmail = data =>
  Provider(CREATE, 'users/validate', {
    data,
  });

const createRegistration = data =>
  Provider(CREATE, 'users/registrations', {
    data,
  });

const updateRegistration = (id, data) =>
  Provider(UPDATE, 'users/registrations', {
    id,
    data,
  });

const updateUser = (id, data) =>
  Provider(UPDATE, 'users', {
    id,
    data,
  });

const createUserProfile = data =>
  Provider(CREATE, 'profiles', {
    data,
  });

const createCheckout = data =>
  Provider(CREATE, 'checkouts', {
    data,
  });

const completeCheckout = (query, data) =>
  Provider(CREATE, 'stripecheckout', {
    data,
    queryParams: {
      ...query,
    },
  });

const resetUserPassword = email =>
  Provider(CREATE, 'resetPassword', {
    data: {
      email,
      url: `${config.FRONTBASE_URL}${route.RESET_PASSWORD}`,
    },
  });

const updateUserPassword = (key, password) =>
  Provider(UPDATE, 'updatePassword', {
    id: key,
    data: {
      password,
    },
  });

const changePassword = (id, oldPassword, newPassword) =>
  Provider(UPDATE, 'users', {
    id,
    data: {
      id,
      password: newPassword,
      passwordValidation: oldPassword,
    },
  });

const checkPasswordKeyStatus = key => Provider(GET_ONE, `updatePassword`, { id: key });

const getWorks = (query, pagination) =>
  Provider(GET_LIST, 'works', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getArticle = articleId =>
  Provider(GET_ONE, 'articles', {
    id: articleId,
  });

const getArticles = query =>
  Provider(GET_LIST, 'articles', {
    queryParams: query,
    pagination: {
      limit: 20,
    },
  });

const getArticlesAggregations = ({ type, query = null, abortSignal, ...restQueryParams }) =>
  Provider(
    GET_LIST,
    'articles/aggregations',
    {
      queryParams: {
        aggregation_type: type,
        aggregation_query: query,
        ...restQueryParams,
      },
      pagination: {
        limit: 20,
        page: 1,
      },
    },
    abortSignal,
  );

const getProductions = (query, pagination) =>
  Provider(GET_LIST, 'productions', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getPerformances = (query, pagination) =>
  Provider(GET_LIST, 'performances', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getCompetitionInfo = (competitionId, queryParams) =>
  Provider(GET_ONE, COMPETITIONS, {
    id: competitionId,
    queryParams,
  });

const searchProfiles = (profileType = ARTIST_TYPE_ID, queryString = '', pagination, filters = {}, from) => {
  const url = from ? 'profiles/search' : 'profiles';
  return Provider(GET_LIST, url, {
    queryParams: {
      query: queryString,
      profile_type_id: profileType,
      ...filters,
    },
    pagination: {
      limit: 10,
      page: 1,
      ...pagination,
    },
  });
};

const getProfile = (profileType = ARTIST_TYPE_ID, queryString = '', pagination) =>
  Provider(GET_LIST, 'profiles', {
    queryParams: {
      name: queryString,
      profile_type_id: profileType,
    },
    pagination: {
      ...pagination,
    },
  });

const getAgencies = (query, pagination) =>
  Provider(GET_LIST, 'agencies', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getAgency = (agencyId, query) =>
  Provider(GET_ONE, 'agencies', {
    id: agencyId,
    queryParams: {
      ...query,
    },
  });

const updateAgency = (agencyId, data, query) =>
  Provider(UPDATE, 'agencies', {
    id: agencyId,
    data,
    queryParams: {
      ...query,
    },
  });

const getOffices = (query, pagination) =>
  Provider(GET_LIST, 'offices', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const deleteAgencyOffice = (officeId, agencyId, profileId) =>
  Provider(DELETE, 'offices', {
    id: officeId,
    queryParams: {
      agency_id: agencyId,
      profile_id: profileId,
    },
  });

const updateAgencyOffice = (officeId, agencyId, profileId, data) =>
  Provider(UPDATE, 'offices', {
    id: officeId,
    queryParams: {
      agency_id: agencyId,
      profile_id: profileId,
    },
    data,
  });

const createAgencyOffice = (agencyId, profileId, data) =>
  Provider(CREATE, 'offices', {
    queryParams: {
      agency_id: agencyId,
      profile_id: profileId,
    },
    data,
  });

const getArtistProfessions = (query, pagination = {}) =>
  Provider(GET_LIST, 'professions', {
    queryParams: query,
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getAgencyArtists = (agencyId, query, pagination) =>
  Provider(GET_LIST, 'agencyArtists', {
    queryParams: {
      agency_id: agencyId,
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getAgencyArtist = (agencyId, artistId, query) =>
  Provider(GET_ONE, 'agencyArtists', {
    id: artistId,
    queryParams: {
      agency_id: agencyId,
      ...query,
    },
  });

const updateAgencyArtist = (agencyId, artistId, data, query) =>
  Provider(UPDATE, 'agencyArtists', {
    id: artistId,
    queryParams: {
      agency_id: agencyId,
      ...query,
    },
    data,
  });

const getArtistsAgreements = (connectionsIds, pagination) =>
  Provider(GET_LIST, 'agreements', {
    queryParams: {
      agency_connection_id: connectionsIds,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const createArtistsAgreement = (artistId, data, activeProfileId) =>
  Provider(CREATE, 'agreements', {
    data: {
      connection: {
        id: artistId,
      },
      ...data,
    },
    queryParams: {
      profile_id: activeProfileId,
    },
  });

const updateArtistAgreement = (artistId, agreement, activeProfileId) =>
  Provider(UPDATE, 'agreements', {
    id: agreement.id,
    data: {
      connection: {
        id: artistId,
      },
      ...agreement,
    },
    queryParams: {
      profile_id: activeProfileId,
    },
  });

const deleteArtistAgreement = (agreementId, activeProfileId) =>
  Provider(DELETE, 'agreements', {
    id: agreementId,
    queryParams: {
      profile_id: activeProfileId,
    },
  });

const removeArtistFromRoster = (agencyId, artistId) =>
  Provider(DELETE, 'agencyArtists', {
    id: artistId,
    queryParams: {
      agency_id: agencyId,
    },
  });

const addArtistToAgencyRoster = (agencyId, profileId, data) =>
  Provider(CREATE, 'agencyArtists', {
    queryParams: {
      agency_id: agencyId,
      profile_id: profileId,
    },
    data,
  });

const getAgencyAgents = (agencyId, query) =>
  Provider(GET_LIST, 'agencyAgents', {
    queryParams: {
      agency_id: agencyId,
      ...query,
    },
  });

const getAggregations = (endpoint, aggParams, pagination = {}, abortSignal) => {
  const endpointUpdated = endpoint || PRODUCTIONS_AGGREGATIONS;
  return Provider(
    GET_LIST,
    endpointUpdated,
    {
      queryParams: aggParams,
      pagination: {
        page: 1,
        limit: 10,
        ...pagination,
      },
    },
    abortSignal,
  );
};

const getCitiesAggregations = (endpoint, aggParams, pagination = {}) => {
  const endpointUpdated = endpoint || PRODUCTIONS_AGGREGATIONS;
  return Provider(GET_LIST, endpointUpdated, {
    queryParams: aggParams,
    pagination: {
      page: 1,
      limit: 10,
      ...pagination,
    },
  });
};

const getRentals = query =>
  Provider(GET_LIST, 'rentals', {
    queryParams: query,
    pagination: {
      limit: 20,
    },
  });

const getRentalsAggregations = ({ type, query = null, ...restQueryParams }) =>
  Provider(GET_LIST, FILTER_AGGREGATION_SEARCH('rentals'), {
    queryParams: {
      aggregation_type: type,
      aggregation_query: query,
      ...restQueryParams,
    },
    pagination: {
      limit: 20,
      page: 1,
    },
  });

const getRental = (rentalId, query) =>
  Provider(GET_ONE, 'rentals', {
    id: rentalId,
    queryParams: query,
  });

const getCountries = (query, slug = {}) =>
  Provider(GET_LIST, 'countries', {
    queryParams: {
      ...slug,
      query,
    },
    pagination: {
      limit: 500,
    },
  });

const getRegions = query =>
  Provider(GET_LIST, 'regions', {
    queryParams: {
      query,
    },
    pagination: {
      limit: 500,
    },
  });

const getCountriesWith = (section, query = {}, pagination = {}) =>
  Provider(GET_LIST, 'countries', {
    queryParams: {
      has: section,
      ...query,
    },
    pagination: {
      limit: 1000,
      ...pagination,
    },
  });

const getCountry = countryId =>
  Provider(GET_ONE, 'countries', {
    id: countryId,
  });

const getCity = cityId =>
  Provider(GET_ONE, 'countries/cities', {
    id: cityId,
  });

const getCompanies = (query, pagination) =>
  Provider(GET_LIST, 'companies', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 12,
      ...pagination,
    },
  });

const getCompany = companyId =>
  Provider(GET_ONE, 'companies', {
    id: companyId,
  });

const getSettings = userId =>
  Provider(GET_LIST, 'settings', {
    queryParams: {
      user_id: userId,
    },
  });

const updateSettingsField = (userId, identified, value) =>
  Provider(UPDATE, ACCOUNT_SETTINGS(userId), {
    id: identified,
    data: {
      value,
    },
  });

const getLanguagesList = () =>
  Provider(GET_LIST, 'languages', {
    queryParams: {
      lang: 'en',
    },
    pagination: {
      limit: 200,
    },
  });

const getStatistics = (typeId, query) =>
  Provider(GET_LIST, 'stats/advanced', {
    queryParams: {
      id: typeId,
      ...query,
    },
  });

const getRepertoire = (query, pagination) =>
  Provider(GET_LIST, 'repertoire', {
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getReviews = (profileId, pagination, query) =>
  Provider(GET_LIST, 'reviews', {
    queryParams: {
      profile_id: profileId,
      ...query,
    },
    pagination: {
      limit: 100,
      page: 1,
      ...pagination,
    },
  });

const getProductsList = () =>
  Provider(GET_LIST, 'products', {
    queryParams: {
      lang: 'en',
    },
    pagination: {
      limit: 100,
    },
  });

const getUserSubscriptions = userId =>
  Provider(GET_LIST, 'users/subscriptions', {
    queryParams: {
      lang: 'en',
      id: userId,
    },
    pagination: {
      limit: 100,
    },
  });

const cancelUserSubscription = (userId, subscriptionId) =>
  Provider(DELETE, 'users/subscriptions', {
    queryParams: {
      lang: 'en',
      id: userId,
      subid: subscriptionId,
    },
  });

const addUserSubscription = (userId, data) =>
  Provider(CREATE, 'users/subscriptions', {
    queryParams: {
      lang: 'en',
      id: userId,
    },
    data,
  });

const getUserBankCards = userId =>
  Provider(GET_LIST, 'users/cards', {
    queryParams: {
      user_id: userId,
    },
    pagination: {
      limit: 100,
    },
  });

const addUserBankCard = (userId, data) =>
  Provider(CREATE, `users/cards`, {
    queryParams: {
      id: userId,
    },
    data,
  });

const deleteUserBankCard = (userId, cardId) =>
  Provider(DELETE, `users/cards`, {
    queryParams: {
      userId,
      cardId,
    },
  });

const updateUserBankCard = (userId, cardId, data) =>
  Provider(UPDATE, `users/cards`, {
    queryParams: {
      userid: userId,
      cardid: cardId,
    },
    data,
  });

const getHomeNumbers = ({ id = 'all' }) => Provider(GET_ONE, 'stats/numbers', { id });

const getOnthisday = (id, query, pagination = {}) =>
  Provider(GET_ONE, 'stats/onthisday', {
    id,
    queryParams: {
      ...query,
    },
    pagination: {
      limit: 1000,
      ...pagination,
    },
  });

const remoteLogout = () => Provider(DELETE, 'logout', {});

const getList = (endpoint, params) => Provider(GET_LIST, endpoint, params);

const getOne = (endpoint, id, params) => Provider(GET_ONE, endpoint, merge({ id }, params));

const updateOne = (endpoint, id, data, files) => Provider(UPDATE, endpoint, { id, data, files });

const deleteOne = (endpoint, id, params) =>
  Provider(DELETE, endpoint, {
    id,
    queryParams: params,
  });

const create = (endpoint, data, files) => Provider(CREATE, endpoint, { data, files });

const getArtistAgencies = (query, pagination = {}) =>
  Provider(GET_LIST, `agencies/artists`, {
    queryParams: query,
    pagination: {
      limit: 10,
      page: 1,
      ...pagination,
    },
  });

export {
  login,
  autoLogin,
  refreshToken,
  getConstants,
  getUser,
  getProfilePermissions,
  getUserPermissions,
  getWork,
  getProfiles,
  getAccessRequests,
  getProfileData,
  getAccessRequestByToken,
  getActiveProfile,
  registerUser,
  validateEmail,
  createRegistration,
  updateRegistration,
  updateUser,
  createUserProfile,
  createCheckout,
  completeCheckout,
  resetUserPassword,
  updateUserPassword,
  changePassword,
  checkPasswordKeyStatus,
  getWorks,
  getArticle,
  getArticles,
  getArticlesAggregations,
  getProductions,
  getPerformances,
  getCompetitionInfo,
  searchProfiles,
  getProfile,
  getAgencies,
  getAgency,
  updateAgency,
  getOffices,
  deleteAgencyOffice,
  updateAgencyOffice,
  createAgencyOffice,
  getArtistProfessions,
  getAgencyArtists,
  getAgencyArtist,
  updateAgencyArtist,
  getArtistsAgreements,
  createArtistsAgreement,
  updateArtistAgreement,
  deleteArtistAgreement,
  removeArtistFromRoster,
  addArtistToAgencyRoster,
  getAgencyAgents,
  getAggregations,
  getCitiesAggregations,
  getRentals,
  getRentalsAggregations,
  getRental,
  getCountries,
  getCountry,
  getCity,
  getRegions,
  getCountriesWith,
  getCompanies,
  getCompany,
  getSettings,
  updateSettingsField,
  getLanguagesList,
  getStatistics,
  getRepertoire,
  getReviews,
  getProductsList,
  getUserSubscriptions,
  cancelUserSubscription,
  addUserSubscription,
  getUserBankCards,
  addUserBankCard,
  deleteUserBankCard,
  updateUserBankCard,
  getHomeNumbers,
  getOnthisday,
  remoteLogout,
  getList,
  getOne,
  updateOne,
  deleteOne,
  create,
  getArtistAgencies,
};
